export const HISTORY_DAYS = [30, 60, 90] as const;
export type HistoryDays = (typeof HISTORY_DAYS)[number];

/** Extends CheckResult with the UI-only no-run state (missing snapshot for a day). */
export const HISTORY_STATUSES = ['passed', 'failed', 'no-run'] as const;
export type HistoryStatus = (typeof HISTORY_STATUSES)[number];

export interface TeamHistoryDailyDto {
  date: string; // 'YYYY-MM-DD' UTC
  /** Failing check-entity pairs keyed by level ID, e.g. { l0: 2, l1: 5 }. */
  levels: Record<string, number>;
  /** Failing pairs whose check has no level assigned (null level_id). */
  others: number;
  noRun: boolean; // true when zero snapshots for any in-scope pair this day
}

export interface TeamHistoryCheckDto {
  checkId: string;
  name: string;
  /** Opaque severity ID, e.g. 's0'. Map to label via severityDisplay() + useSeverities(). */
  severity: string;
  trackId: string | null;
  trackName: string | null;
  /** Opaque level ID, e.g. 'l0'. null when the check has no level assigned. */
  levelId: string | null;
  /** Resolved level name, e.g. 'Foundation'. null when levelId is null. */
  levelName: string | null;
  inScopeCount: number;
  failingCount: number;
  daily: Array<{ date: string; failing: number; inScope: number }>;
}

export interface TeamHistoryKpisDto {
  failingChecks: number;
  failingProjects: number;
  fixed: number;
  longestOpen: { days: number; check: string; project: string } | null;
  /** Check-entity pairs passing at their most-recent snapshot in the period. */
  passed: number;
  /** Total check-entity pairs with any data in the period. */
  total: number;
}

export interface TeamHistoryDto {
  from: string; // 'YYYY-MM-DD' UTC
  to: string;
  earliestSnapshotAt: string | null; // ISO timestamp
  kpis: TeamHistoryKpisDto;
  daily: TeamHistoryDailyDto[];
  checks: TeamHistoryCheckDto[]; // only checks with failingCount > 0
}

export interface TeamCheckSegmentDto {
  from: string;
  to: string;
  status: HistoryStatus;
}

export interface TeamCheckHistoryRowDto {
  projectPath: string;
  projectName: string;
  entityUrl: string;
  failingDays: number;
  /** True when the project's most recent snapshot (ignoring trailing no-run days) is failed. */
  currentlyFailing: boolean;
  segments: TeamCheckSegmentDto[];
}

export interface TrackWeeklyScoreDto {
  /** Monday of the ISO week, UTC — 'YYYY-MM-DD'. */
  weekOf: string;
  /**
   * Progress 0–100: round(100 × checks where ALL in-scope entities pass / total checks that week).
   * Same all-projects-must-pass rule as the current-snapshot progress metric.
   */
  progress: number;
}

export interface TrackTrendDto {
  /** Matches TeamTrackSummaryDto.id; null = Uncategorized. */
  trackId: string | null;
  weeks: TrackWeeklyScoreDto[];
}
